<template>
    <div class="dynamic-pricing-rule-component">
        <div :class="loadMask"></div>
        <div>
            <div class="row" v-if="errors.message">
                <div class="col-lg-12">
                    <span class="error">{{ t(errors.message) }}</span>
                </div>
            </div>

            <!-- Selection section -->
            <div class="row">
              <div class="col-lg-12">
                <label>{{ t('Selected products') }}</label>
                <ul>
                  <li>Wybrano {{ getSelected }} z {{ getTotalRows }} produkt/ów</li>
                </ul>
                <span v-if="hasError('selectedRows')" class="error">{{ t(errors.selectedRows) }}</span>
              </div>
            </div>

            <!-- Price rule section -->

            <div class="row">
              <div class="col-lg-6 form-group">
                <label>{{ t('Choose price rule') }}</label>
                <multiselect
                    :placeholder="t('Select')"
                    :options="availablePriceRules"
                    :multiple="false"
                    :limit="10"
                    :options-limit="1000"
                    :limit-text="limitText"
                    :close-on-select="true"
                    :clear-on-select="false"
                    track-by="id"
                    label="name"
                    :loading="priceRulesLoadingFilter"
                    v-model="inputs.priceRule"
                    @open="onOpenPriceRules"
                >
                  <div slot="noOptions">{{ t('List is empty') }}</div>
                </multiselect>
              </div>
              <span v-if="hasError('priceRule')" class="error">{{ t(errors.priceRule) }}</span>
            </div>
            <!-- end Price rule section -->

            <div class="row">
                <div class="col-lg-12">
                    <button class="btn btn-success btn-confirm"
                            @click.prevent="submit"
                    >{{ t('Submit') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Multiselect from "@/components/Multiselect.vue";
import State from "@/Models/State.ts";
import ValueChecker from "@/services/ValueChecker";

export default {
    components: {
        Multiselect
    },
    props: {
        config: {
            Type: Object
        },
        eventData: {
            Type: Object
        },
        eventBus: {
            type: Object,
        },
    },
    data: function () {
        return {
            availablePriceRules: [],
            priceRulesLoadingFilter: false,
            loadMask: '',
            inputs: {
                priceRule: [],
                allSelected: false,
                selected: [],
                excluded: [],
            },
            errors: {},
            events: [],
            limitText(count) {
                return this.$trans('and %count% other items', {count: count});
            },
            stateOrigin: new State()
        };
    },
    beforeDestroy() {
    },
    async mounted() {
        this.init();
        this.stateOrigin.value = this.inputs;
    },
    computed: {
        getSelected() {
          return this.$store.getters.getRecordsNumberForSelections('DynamicPricingGridComponent');
        },
        getTotalRows() {
          return this.$store.getters.getTotalRecordsNumberForSelections('DynamicPricingGridComponent');
        }
    },
    methods: {
        init() {
            let selection = this.$store.getters.getSelection('DynamicPricingGridComponent');

            this.inputs.selected = selection.selected;
            this.inputs.excluded = selection.excluded;
            this.inputs.allSelected = selection.allSelected;
            this.inputs.filters = selection.filters;
        },
        async getAvailablePriceRules() {
            const url = this.config['priceRulesFilterUrl'];
            let response = await this.$http['get'](url, {});
            this.availablePriceRules = response.data.data;
        },
        async onOpenPriceRules() {
            if (0 < this.availablePriceRules.length) {
                return;
            }

            this.priceRulesLoadingFilter = true;
            await this.getAvailablePriceRules();
            this.priceRulesLoadingFilter = false;
        },
        async submit() {
            this.addLoadMask();

            if (!await this.validate()) {
                this.removeLoadMask();

                return;
            }

            let formData = this.getFormData();
            let url = this.getSubmitUrl();
            let response;
            let params = {};

            try {
                response = await this.$http['post'](url, formData, params);
                this.stateOrigin.resetState(this.inputs);
                let eventManagerService = this.eventBus.getEventManager();
                let afterSubmitEvents = this.config.emitEvents['afterSubmit'];

                if (afterSubmitEvents) {
                    eventManagerService.emitEvents(afterSubmitEvents, [{
                        eventData: this.eventData
                    }]);
                }
            } catch (error) {
                this.addError('message', error.data.message);
            } finally {
                this.removeLoadMask();
            }
        },
        async validate() {
            for (let member in this.errors) delete this.errors[member];

            if (!this.inputs.allSelected && 0 === this.inputs.selected.length) {
                this.addError('selectedRows', 'Please select at least one product');
            }

            this.validatePriceRule(this.inputs.priceRule);

            return 0 === Object.keys(this.errors).length;
        },
        validatePriceRule(priceRule) {
            if (ValueChecker.isArray(priceRule) && 0 === priceRule.length) {
                this.addRequiredError('priceRule');

                return;
            }

            if (ValueChecker.isEmptyObject(priceRule)) {
                this.addRequiredError('priceRule');
            }
        },
        getFormData() {
            return {
              priceRule: this.inputs.priceRule.id,
              allSelected: this.inputs.allSelected,
              selected: this.inputs.selected,
              excluded: this.inputs.excluded,
              filters: this.inputs.filters,
            };
        },
        addLoadMask() {
            this.loadMask = 'lmask';
        },
        removeLoadMask() {
            this.loadMask = '';
        },
        addRequiredError(key) {
            this.addError(key, this.$trans('This field is required'));
        },
        addError(key, value) {
            this.$set(this.errors, key, value);
        },
        clearError(key) {
            this.$set(this.errors, key, undefined);
            delete this.errors[key];
        },
        hasError(key) {
            return key in this.errors;
        },
        getSubmitUrl() {
            return this.config['editUrl'];
        },
        async canCloseModal() {
            if (!this.stateOrigin.isEqual(this.inputs)) {
                return await this.hasInputsChange();
            }

            return true;
        },
        hasInputsChange() {
            return new Promise((resolve) => {
                this.$root.showConfirmModalResolve('Confirm?', resolve);
            });
        }
    }
};
</script>

<style scoped>
.dynamic-pricing-rule-component {
    flex: 1;
}

input[type=checkbox], input[type=radio] {
    margin: 4px 0 0;
    margin-top: 1px \9;
    line-height: normal;
    box-shadow: none;
}

input[type=number] {
    box-shadow: none;
    border-radius: 5px;
}

.error {
    color: red;
}

.row.align-items-end {
    display: flex;
    align-items: flex-end;
}

</style>
<style>

.multiselect__placeholder {
    padding-top: 0 !important;
}
</style>



// WEBPACK FOOTER //
// DynamicPricingAssignRuleToProductComponent.vue?7ba022bd